import type { ChartLikeProtocol, ChartOptionProtocol, PieChartOptionProtocol, SingleChartOptionProtocol } from '@lighthouse/core'

import type { ECOption } from '../../components'
import { defaultColors, legendOffset, legendWidth } from './common'

type PieSeriesDataItem = {
    value: number
    name: string
}

type LegendStat = {
    value: number
    percent: string
}

const normalizeChartNumericValue = (value: unknown) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0
    }

    if (typeof value === 'string') {
        const normalizedValue = value.trim().replace(/,/g, '')
        if (!normalizedValue) {
            return 0
        }

        const parsedValue = Number(normalizedValue)
        return Number.isFinite(parsedValue) ? parsedValue : 0
    }

    return 0
}

const formatLegendPercent = (percent: number) => {
    if (!Number.isFinite(percent) || percent <= 0) {
        return '0%'
    }

    return `${Number(percent.toFixed(2))}%`
}

const getSeriesData = (option: ChartOptionProtocol, chartData: ChartLikeProtocol): PieSeriesDataItem[] => {
    const { series, xAxis } = chartData

    if (series.length === 0) {
        return []
    }

    const [firstSeries] = series
    const { value: seriesValue } = firstSeries

    if (seriesValue.length > 1) {
        return xAxis.map((item, index) => ({
            value: normalizeChartNumericValue(seriesValue[index]),
            name: item[0] || '空'
        }))
    }

    if (seriesValue.length === 1) {
        if (option.chartType === 'pie') {
            return series.map(item => ({
                value: normalizeChartNumericValue(item.value?.[0]),
                name: xAxis[0]?.[0] || '空'
            }))
        }
        return series.map(item => ({
            value: normalizeChartNumericValue(item.value?.[0]),
            name: item.name
        }))
    }

    return series.map(item => ({
        value: normalizeChartNumericValue(item.value?.[0]),
        name: item.name
    }))
}

const getLegendStats = (seriesData: PieSeriesDataItem[]) => {
    const valueByName = new Map<string, number>()

    seriesData.forEach(({ name, value }) => {
        valueByName.set(name, (valueByName.get(name) || 0) + value)
    })

    const total = Array.from(valueByName.values()).reduce((sum, value) => sum + value, 0)

    return new Map<string, LegendStat>(
        Array.from(valueByName.entries()).map(
            ([name, value]) =>
                [
                    name,
                    {
                        value,
                        percent: formatLegendPercent(total > 0 ? (value / total) * 100 : 0)
                    }
                ] as const
        )
    )
}

const formatLegendLabel = (name: string, legendStats: Map<string, LegendStat>) => {
    const stat = legendStats.get(name)

    if (!stat) {
        return name
    }

    return `${name} ${stat.value} ${stat.percent}`
}

const getLegend = (params: PieChartOptionProtocol, chartData: ChartLikeProtocol): ECOption['legend'] => {
    const { showLegend, legendTranspose, dimensions } = params
    const { legend, xAxis } = chartData
    const data = dimensions.length > 0 ? xAxis.map(item => item[0]).filter(Boolean) : legend
    const legendStats = getLegendStats(getSeriesData(params, chartData))
    const legendConfigurator: ECOption['legend'] = {
        type: 'scroll',
        show: showLegend,
        data,
        bottom: legendOffset,
        icon: 'circle',
        formatter: name => formatLegendLabel(name, legendStats)
    }
    switch (legendTranspose) {
        case 'top': {
            legendConfigurator.top = legendOffset
            break
        }
        case 'bottom': {
            legendConfigurator.bottom = legendOffset
            break
        }
        case 'left': {
            legendConfigurator.left = legendOffset
            legendConfigurator.top = 'center'
            legendConfigurator.width = legendWidth
            legendConfigurator.orient = 'vertical'
            break
        }
        case 'right': {
            legendConfigurator.right = legendOffset
            legendConfigurator.width = legendWidth
            legendConfigurator.top = 'center'
            legendConfigurator.orient = 'vertical'
            break
        }
        default: {
            break
        }
    }

    return legendConfigurator
}

const getSeries = (option: ChartOptionProtocol, chartData: ChartLikeProtocol): ECOption['series'] => {
    const { legend } = chartData

    const seriesData = getSeriesData(option, chartData)

    if (seriesData.length === 0) {
        return {}
    }
    const [seriesName] = legend

    return {
        name: seriesName,
        type: 'pie',
        data: seriesData,
        bottom: '20px',
        top: '20px',
        emphasis: {
            focus: 'self'
        },
        // label: { show: showLabel },
        blur: {
            itemStyle: {
                opacity: 0.6
            }
        }
    }
}

const getPieCommonOption = (params: SingleChartOptionProtocol) => {
    const { colors, showHeadTitle, title } = params
    const commonOption: Pick<ECOption, 'color' | 'title'> = {
        color: colors?.length > 0 ? colors : defaultColors,
        title: {
            text: showHeadTitle && title ? title : undefined,
            textStyle: {
                fontSize: 24,
                fontWeight: 500,
                color: '#101828'
            }
        }
    }
    return commonOption
}

const getPieOption = (params: PieChartOptionProtocol, chartData?: ChartLikeProtocol, themeColors?: string[]) => {
    if (!chartData) {
        return {}
    }
    const commonOption = getPieCommonOption(params)
    const mergedOption: ECOption = {
        tooltip: {
            show: true,
            formatter: params => {
                const param = Array.isArray(params) ? params[0] : params
                const { marker, name, value, percent, seriesName } = param
                return `<div style="margin-bottom: 12px; font-weight: bold">
                            ${name || '空'}
                        </div>
                        ${marker} ${seriesName}：${String(value)} (${percent}%)`
            }
        },
        ...commonOption,
        legend: getLegend(params, chartData),
        series: getSeries(params, chartData)
    }
    return mergedOption
}

export default getPieOption

// export const getLegendOption: Pick<ECOption, 'legend'> = (showLegend: boolean | undefined,showHeadTitle: boolean, canExport?: boolean) => {
//     if()
//     return {

//     }
// }
